import React, { useState, useEffect } from 'react';
import api from '../api.js';
import { useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const Domains = () => {
  const [domains, setDomains] = useState([]);
  const [urls, setUrls] = useState([]);
  const [slugs, setSlugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchingDomain, setSearchingDomain] = useState(null);
  const [searchStatuses, setSearchStatuses] = useState({});
  const [searchingUrl, setSearchingUrl] = useState(null);
  const [urlSearchStatuses, setUrlSearchStatuses] = useState({});
  const { user } = useAuth();
  const isAdmin = user && user.user_type === 2;

  useEffect(() => {
    fetchDomains();
    fetchUrls();
    fetchSlugs();
  }, []);

  useEffect(() => {
    if (domains.length > 0) {
      const fetchStatuses = async () => {
        const statuses = {};
        await Promise.all(domains.map(async (domain) => {
          try {
            const res = await api.getSearchStatus(domain['name']);
            statuses[domain['name']] = res.searching;
          } catch {
            statuses[domain['name']] = false;
          }
        }));
        setSearchStatuses(statuses);
      };
  
      fetchStatuses();
  
      const interval = setInterval(fetchStatuses, 5000); // fetch status every 5 seconds
      return () => clearInterval(interval);
    }
  }, [domains]);

  useEffect(() => {
    if (urls.length > 0) {
      const fetchUrlStatuses = async () => {
        const statuses = {};
        await Promise.all(urls.map(async (url) => {
          try {
            const res = await api.getUrlSearchStatus(url['name']);
            statuses[url['name']] = res.searching;
          } catch {
            statuses[url['name']] = false;
          }
        }));
        setUrlSearchStatuses(statuses);
      };
  
      fetchUrlStatuses();
  
      const interval = setInterval(fetchUrlStatuses, 5000); // fetch status every 5 seconds
      return () => clearInterval(interval);
    }
  }, [urls]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const data = await api.getDomains();
      setDomains(data.domains || []);
    } catch (err) {
      setError('Failed to load domains');
      console.error('Domains error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUrls = async () => {
    try {
      const data = await api.getUrls();
      setUrls(data.urls || []);
    } catch (err) {
      console.error('URLs error:', err);
      // Don't set error for URLs as it's not critical
    }
  };

  const fetchSlugs = async () => {
    try {
      const data = await api.getSlugs();
      setSlugs(data.slugs || []);
    } catch (err) {
      console.error('Slugs error:', err);
    }
  };

  // Split domains by domain_type
  const primaryDomains = domains.filter(domain => domain['domain_type'] === 1);
  const thirdPartyDomains = domains.filter(domain => domain['domain_type'] === 2);

  // Split URLs by url_type
  const employeeUrls = urls.filter(url => url['url_type'] & 1);
  const userUrls = urls.filter(url => url['url_type'] & 2);

  const handleRowClick = (domain) => {
    navigate(`/domains/${encodeURIComponent(domain['name'])}`);
  };

  const handleSearchNow = async (domain) => {
    setSearchingDomain(domain['name']);
    try {
      await api.searchDomainNow(domain['name']);
      // Query the backend for the actual search status
      try {
        const res = await api.getSearchStatus(domain['name']);
        setSearchStatuses(prev => ({
          ...prev,
          [domain['name']]: res.searching
        }));
      } catch (statusErr) {
        console.error('Failed to get search status:', statusErr);
        // If we can't get the status, assume it's not searching
        setSearchStatuses(prev => ({
          ...prev,
          [domain['name']]: false
        }));
      }
    } catch (err) {
      setError('Failed to start search');
      console.error('Search now error:', err);
    } finally {
      setSearchingDomain(null);
    }
  };

  const handleSearchUrlNow = async (url) => {
    setSearchingUrl(url['name']);
    try {
      await api.searchUrlNow(url['name']);
      // Query the backend for the actual search status
      try {
        const res = await api.getUrlSearchStatus(url['name']);
        setUrlSearchStatuses(prev => ({
          ...prev,
          [url['name']]: res.searching
        }));
      } catch (statusErr) {
        console.error('Failed to get URL search status:', statusErr);
        // If we can't get the status, assume it's not searching
        setUrlSearchStatuses(prev => ({
          ...prev,
          [url['name']]: false
        }));
      }
    } catch (err) {
      setError('Failed to start URL search');
      console.error('URL search now error:', err);
    } finally {
      setSearchingUrl(null);
    }
  };

  const handleUrlRowClick = (url) => {
    navigate(`/urls/${encodeURIComponent(url['name'])}`);
  };

  const handleSlugRowClick = (slug) => {
    navigate(`/slugs/${encodeURIComponent(slug['name'])}`);
  };

  const renderTable = (domainsArray, title) => (
    <div style={{
      background: 'var(--white)',
      borderRadius: '12px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      border: '1px solid var(--border)',
      overflow: 'hidden',
      marginBottom: '2rem'
    }}>
      <h2 style={{ padding: '1rem', margin: 0, color: 'var(--text)' }}>{title}</h2>
      {domainsArray.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ background: 'var(--secondary-color)' }}>
                <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: isAdmin ? '50%' : '55%' }}>
                  Domain
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: '20%' }}>
                  Frequency
                </th>
                <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: isAdmin ? '25%' : '30%' }}>
                  Last Update
                </th>
                {isAdmin && <th style={{ width: '5%', padding: '1rem' }}></th>}
              </tr>
            </thead>
            <tbody>
              {domainsArray.map((domain, index) => (
                <tr key={index} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                  <td style={{ padding: '1rem', fontWeight: '500', display: 'flex', alignItems: 'center', width: isAdmin ? '50%' : '55%' }} onClick={() => handleRowClick(domain)}>
                    @{String(domain['name'] || '').replace(/^@+/, '')}
                  </td>
                  <td style={{ padding: '1rem', width: '20%' }} onClick={() => handleRowClick(domain)}>
                    {domain['frequency'] === -1 ? 'Never' :
                     domain['frequency'] === 0 && domain['week_day'] === 0 ? 'Monthly' :
                     domain['frequency'] === 0 ? 'Weekly' :
                     domain['frequency'] === 1 ? '1x/Day' :
                     domain['frequency'] === 2 ? '2x/Day' :
                     domain['frequency'] === 3 ? '3x/Day' :
                     domain['frequency'] === 4 ? '4x/Day' :
                     domain['frequency'] === 6 ? '6x/Day' :
                     domain['frequency'] === 8 ? '8x/Day' :
                     domain['frequency'] === 12 ? '12x/Day' :
                     'Every Hour'}
                  </td>
                  <td style={{ padding: '1rem', width: isAdmin ? '25%' : '30%' }} onClick={() => handleRowClick(domain)}>
                    {domain['last_updated'] ? new Date(domain['last_updated']).toLocaleString() : 'Never'}
                  </td>
                  {isAdmin && (
                    <td style={{ padding: '1rem', textAlign: 'center', width: '5%' }}>
                     {(searchingDomain === domain['name'] || searchStatuses[domain['name']]) ? (
                       <span className="spinner" style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary-color)', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} title="Searching..." />
                     ) : (
                       <button
                         style={{
                           background: 'none',
                           border: 'none',
                           cursor: 'pointer',
                           outline: 'none',
                           padding: 0,
                           fontSize: '1.2rem',
                           color: 'var(--secondary-color)'
                         }}
                         title="Search now"
                         onClick={e => { e.stopPropagation(); handleSearchNow(domain); }}
                       >
                         <FaSearch style={{ color: 'var(--primary-color)' }} />
                       </button>
                     )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
          No domains found.
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '1.2rem', color: 'var(--text-light)' }}>
            Loading domains...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: 'var(--text)', margin: 0 }}>
          Domains
        </h1>
        <p style={{ color: 'var(--text-light)' }}>
          View your monitored domains, URLs and slugs for threat intelligence collection
        </p>
      </div>
      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      {renderTable(primaryDomains, 'Primary Domains')}
      {renderTable(thirdPartyDomains, 'Third-Party Domains')}
      
      {/* URL Tables */}
      {(
        <div style={{
          background: 'var(--white)',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
          marginBottom: '2rem'
        }}>
          <h2 style={{ padding: '1rem', margin: 0, color: 'var(--text)' }}>Employee URLs</h2>
          {employeeUrls.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ background: 'var(--secondary-color)' }}>
                    <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: isAdmin ? '50%' : '55%' }}>
                      URL
                    </th>
                    <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: '20%' }}>
                      Frequency
                    </th>
                    <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: isAdmin ? '25%' : '30%' }}>
                      Last Update
                    </th>
                    {isAdmin && <th style={{ width: '5%', padding: '1rem' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {employeeUrls.map((url, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                      <td style={{ padding: '1rem', fontWeight: '500', width: isAdmin ? '50%' : '55%' }} onClick={() => handleUrlRowClick(url)}>
                        {url['name']}
                      </td>
                      <td style={{ padding: '1rem', width: '20%' }} onClick={() => handleUrlRowClick(url)}>
                        {url['frequency'] === -1 ? 'Never' :
                         url['frequency'] === 0 && url['week_day'] === 0 ? 'Monthly' :
                         url['frequency'] === 0 ? 'Weekly' :
                         url['frequency'] === 1 ? '1x/Day' :
                         url['frequency'] === 2 ? '2x/Day' :
                         url['frequency'] === 3 ? '3x/Day' :
                         url['frequency'] === 4 ? '4x/Day' :
                         url['frequency'] === 6 ? '6x/Day' :
                         url['frequency'] === 8 ? '8x/Day' :
                         url['frequency'] === 12 ? '12x/Day' :
                         'Every Hour'}
                      </td>
                      <td style={{ padding: '1rem', width: isAdmin ? '25%' : '30%' }} onClick={() => handleUrlRowClick(url)}>
                        {url['last_updated'] ? new Date(url['last_updated']).toLocaleString() : 'Never'}
                      </td>
                      {isAdmin && (
                        <td style={{ padding: '1rem', textAlign: 'center', width: '5%' }}>
                          {(searchingUrl === url['name'] || urlSearchStatuses[url['name']]) ? (
                            <span className="spinner" style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary-color)', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} title="Searching..." />
                          ) : (
                            <button
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                outline: 'none',
                                padding: 0,
                                fontSize: '1.2rem',
                                color: 'var(--secondary-color)'
                              }}
                              title="Search now"
                              onClick={e => { e.stopPropagation(); handleSearchUrlNow(url); }}
                            >
                              <FaSearch style={{ color: 'var(--primary-color)' }} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
              No URLs found.
            </div>
          )}
        </div>
      )}

      {(
        <div style={{
          background: 'var(--white)',
          borderRadius: '12px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
          marginBottom: '2rem'
        }}>
          <h2 style={{ padding: '1rem', margin: 0, color: 'var(--text)' }}>User URLs</h2>
          {userUrls.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                  <tr style={{ background: 'var(--secondary-color)' }}>
                    <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: isAdmin ? '50%' : '55%' }}>
                      URL
                    </th>
                    <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: '20%' }}>
                      Frequency
                    </th>
                    <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: isAdmin ? '25%' : '30%' }}>
                      Last Update
                    </th>
                    {isAdmin && <th style={{ width: '5%', padding: '1rem' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {userUrls.map((url, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                      <td style={{ padding: '1rem', fontWeight: '500', width: isAdmin ? '50%' : '55%' }} onClick={() => handleUrlRowClick(url)}>
                        {url['name']}
                      </td>
                      <td style={{ padding: '1rem', width: '20%' }} onClick={() => handleUrlRowClick(url)}>
                        {url['frequency'] === -1 ? 'Never' :
                         url['frequency'] === 0 && url['week_day'] === 0 ? 'Monthly' :
                         url['frequency'] === 0 ? 'Weekly' :
                         url['frequency'] === 1 ? '1x/Day' :
                         url['frequency'] === 2 ? '2x/Day' :
                         url['frequency'] === 3 ? '3x/Day' :
                         url['frequency'] === 4 ? '4x/Day' :
                         url['frequency'] === 6 ? '6x/Day' :
                         url['frequency'] === 8 ? '8x/Day' :
                         url['frequency'] === 12 ? '12x/Day' :
                         'Every Hour'}
                      </td>
                      <td style={{ padding: '1rem', width: isAdmin ? '25%' : '30%' }} onClick={() => handleUrlRowClick(url)}>
                        {url['last_updated'] ? new Date(url['last_updated']).toLocaleString() : 'Never'}
                      </td>
                      {isAdmin && (
                        <td style={{ padding: '1rem', textAlign: 'center', width: '5%' }}>
                          {(searchingUrl === url['name'] || urlSearchStatuses[url['name']]) ? (
                            <span className="spinner" style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary-color)', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} title="Searching..." />
                          ) : (
                            <button
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                outline: 'none',
                                padding: 0,
                                fontSize: '1.2rem',
                                color: 'var(--secondary-color)'
                              }}
                              title="Search now"
                              onClick={e => { e.stopPropagation(); handleSearchUrlNow(url); }}
                            >
                              <FaSearch style={{ color: 'var(--primary-color)' }} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
              No URLs found.
            </div>
          )}
        </div>
      )}

      <div style={{
        background: 'var(--white)',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '1px solid var(--border)',
        overflow: 'hidden',
        marginBottom: '2rem'
      }}>
        <h2 style={{ padding: '1rem', margin: 0, color: 'var(--text)' }}>Slugs</h2>
        {slugs.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead>
                <tr style={{ background: 'var(--secondary-color)' }}>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: '60%' }}>
                    Slug
                  </th>
                  <th style={{ textAlign: 'left', padding: '1rem', color: 'var(--text-light)', fontWeight: '600', width: '40%' }}>
                    Last Match
                  </th>
                </tr>
              </thead>
              <tbody>
                {slugs.map((slug) => (
                  <tr key={slug['id']} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => handleSlugRowClick(slug)}>
                    <td style={{ padding: '1rem', fontWeight: '500' }}>
                      {slug['name']}
                    </td>
                    <td style={{ padding: '1rem' }}>
                      {slug['last_updated'] ? new Date(slug['last_updated']).toLocaleString() : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-light)' }}>
            No slugs found.
          </div>
        )}
      </div>
    </div>
  );
};

export default Domains;
